import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CalendarCheck,
  ClipboardList,
  Hammer,
  LineChart,
  Phone,
  Repeat,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/how-we-work`;

const STEPS = [
  {
    icon: Phone,
    name: "Free 30-minute strategy call",
    summary:
      "We start with a real conversation — no slide deck, no sales pitch. Chris reviews your site, your Google Business Profile, and your top competitors live, and tells you what's actually holding you back.",
    deliverables: [
      "Live audit of your website on the call",
      "Live look at your Google Business Profile",
      "Honest read on whether we're the right fit",
      "If we're not, we tell you who is",
    ],
    timeframe: "30 minutes · same week",
  },
  {
    icon: ClipboardList,
    name: "Custom marketing plan",
    summary:
      "We build you a written plan that names exactly what to fix, in what order, and what each piece is worth to your business. No retainers, no mystery line items, no jargon.",
    deliverables: [
      "Prioritized list of fixes (web, SEO, GBP, ads)",
      "Plain-English explanation of why each one matters",
      "Realistic timeline and price for each piece",
      "What you can do yourself vs. what we should do",
    ],
    timeframe: "3–5 business days",
  },
  {
    icon: Hammer,
    name: "Build / fix / launch",
    summary:
      "We do the work in-house, hand-coded, with weekly check-ins. You see progress as it happens — no black-box agency reports, no junior team you've never met.",
    deliverables: [
      "Hand-coded website (no page builders or bloat)",
      "Google Business Profile rebuilt from scratch",
      "On-page SEO + schema + Core Web Vitals",
      "Service-area landing pages where they earn rank",
      "Weekly progress check-ins with the owner",
    ],
    timeframe: "2–6 weeks for most builds",
  },
  {
    icon: LineChart,
    name: "Track, tune, and report",
    summary:
      "We measure what actually moved — calls, form fills, booked jobs — not vanity metrics. Reporting is monthly, plain English, and tied to revenue.",
    deliverables: [
      "Monthly report tied to calls + booked work",
      "Rank tracking for the keywords that matter",
      "Core Web Vitals + mobile performance check",
      "What we changed, what we'll change next",
    ],
    timeframe: "Monthly cadence, ongoing",
  },
  {
    icon: Repeat,
    name: "Refine quarterly",
    summary:
      "Every 90 days we revisit the plan against what the data is showing — what's earning, what isn't, what to expand. You always know what's next and why.",
    deliverables: [
      "Quarterly strategy review with the owner",
      "Updated 90-day plan with revised priorities",
      "Honest assessment of what's not working",
    ],
    timeframe: "Every 90 days",
  },
];

const HOWTO_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "HowTo",
      "@id": `${CANONICAL}#howto`,
      name: "How Colorado Web Impressions works with new clients",
      description:
        "A 5-step engagement process used with every Colorado Web Impressions client — from first call to ongoing optimization.",
      inLanguage: "en-US",
      totalTime: "P30D",
      provider: { "@id": SCHEMA_ORG_ID },
      step: STEPS.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.summary,
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "How We Work", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/how-we-work")({
  head: () => ({
    meta: [
      {
        title:
          "How We Work — Our 5-Step Process | Colorado Web Impressions",
      },
      {
        name: "description",
        content:
          "Our 5-step engagement process — from a free strategy call to quarterly tune-ups. No retainers, no mystery line items, no black-box reporting.",
      },
      {
        property: "og:title",
        content: "How We Work — Colorado Web Impressions",
      },
      {
        property: "og:description",
        content:
          "Strategy call → custom plan → build → measure → refine. Plain English, owner-led, in-house.",
      },
      { property: "og:url", content: CANONICAL },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(HOWTO_LD) },
    ],
  }),
  component: HowWeWorkPage,
});

function HowWeWorkPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Our methodology"
        title={
          <>
            Five steps. <span className="text-brand-gold">No mystery.</span>
          </>
        }
        description="Every Colorado Web Impressions engagement follows the same playbook — written down, repeatable, and tied to outcomes you can measure in calls, form fills, and booked jobs."
      />

      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <ol className="space-y-10 lg:space-y-14">
            {STEPS.map((s, i) => (
              <li
                key={s.name}
                className="grid gap-6 rounded-3xl border border-border bg-card p-6 shadow-card lg:grid-cols-[8rem_minmax(0,1fr)] lg:p-8"
              >
                <div className="flex items-start gap-4 lg:flex-col lg:items-center lg:gap-2">
                  <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-gold text-brand-black shadow-gold">
                    <s.icon className="h-7 w-7" />
                  </div>
                  <div className="lg:text-center">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
                      Step {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-muted-foreground">
                      {s.timeframe}
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-extrabold leading-tight text-foreground">
                    {s.name}
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-foreground/85">
                    {s.summary}
                  </p>
                  <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                    {s.deliverables.map((d) => (
                      <li
                        key={d}
                        className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm text-foreground/85"
                      >
                        <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-14 rounded-3xl border border-border bg-muted/40 p-6 text-center sm:p-10">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
              Want to see step 1 on your own business?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
              Book a free 30-minute strategy call. We'll audit your site,
              Google Business Profile, and top competitors live — and you'll
              walk away with a clear next step whether you hire us or not.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Book a free strategy call
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
